import type {
  FactoryEdit,
  FactoryEditPayload,
  FactoryStatus,
  LegacyContact,
  LegacyFactory,
  LegacyFactoryWriteEnvelope,
  LegacyProjectReference,
  LegacySlugData,
  ProjectReference,
} from '@indutrust/factory-model';
import type { Contact, SlugData } from '@indutrust/shared-model';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * True when the payload already looks like a domain {@link FactoryEdit}
 * (MSW / modern responses) rather than a legacy wire factory.
 */
export function isDomainFactoryEdit(wire: unknown): wire is FactoryEdit {
  return (
    isRecord(wire) &&
    typeof wire['status'] === 'string' &&
    typeof wire['linked_in_url'] !== 'undefined' &&
    Array.isArray(wire['capability_ids'])
  );
}

function toSlugData(items: LegacySlugData[] | undefined): SlugData[] {
  return (items ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    slug: item.slug,
  }));
}

function parseCoordinate(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const normalized = value.replace(',', '.').trim();
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseTurnover(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  // Reject free-text legacy values like "2M euros" — only accept pure numerics.
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseCertification(
  value: string | string[] | null | undefined
): string[] | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (Array.isArray(value)) {
    return value;
  }
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Derives domain `verified` + `status` for the R14 view gate.
 *
 * uae-back often omits both fields on GET /factories/:id. Absence is not an
 * explicit draft — treat as published + verified so {@link gateVisibleFactory}
 * can pass. Explicit `verified: false` or `status: 'draft'|'pending'` still
 * hide the profile.
 */
function deriveVisibility(legacy: LegacyFactory): {
  status: FactoryStatus;
  verified: boolean;
} {
  const hasVerified = typeof legacy.verified === 'boolean';
  const rawStatus = legacy.status;
  const hasStatus =
    rawStatus === 'draft' ||
    rawStatus === 'pending' ||
    rawStatus === 'published';

  if (!hasVerified && !hasStatus) {
    return { status: 'published', verified: true };
  }

  if (hasStatus) {
    return {
      status: rawStatus,
      verified: hasVerified ? !!legacy.verified : rawStatus === 'published',
    };
  }

  const verified = legacy.verified ?? false;
  return {
    status: verified ? 'published' : 'draft',
    verified,
  };
}

/**
 * Collects Technology Capability leaf ids from every legacy sub-entity family.
 *
 * The UI's `capability_ids` map to legacy technology + service + product leaves
 * (CS-2). Ids are de-duplicated — families are separate tables so collisions
 * are rare but possible.
 */
export function collectLegacyLeafIds(wire: LegacyFactory): number[] {
  const ids = [
    ...(wire.sub_technologies ?? []).map((item) => item.id),
    ...(wire.sub_services ?? []).map((item) => item.id),
    ...(wire.sub_products ?? []).map((item) => item.id),
  ];
  return [...new Set(ids)].sort((a, b) => a - b);
}

function mapContact(contact: LegacyContact): Contact {
  const type: Contact['type'] =
    contact.type === 'admin' || contact.type === 'sales'
      ? contact.type
      : 'sales';
  return {
    id: contact.id,
    type,
    email: contact.email,
    number: contact.number,
    has_whatsapp: false,
    country_code: contact.country_code ?? null,
    country_id: contact.country_id ?? null,
    first_name: contact.first_name ?? null,
    indutrustMainPoc: contact.indutrustMainPoc ?? false,
    last_name: contact.last_name ?? null,
  };
}

function mapProjectReference(ref: LegacyProjectReference): ProjectReference {
  return {
    id: ref.id,
    factory_id: ref.factory_id,
    description: ref.description,
    customer_name: ref.customer_name,
    country_id: ref.country_id ?? null,
    start_date: ref.start_date,
    end_date: ref.end_date,
    industrial_subtype_ids: ref.industrial_subtype_ids ?? [],
    capability_ids: ref.capability_ids ?? [],
    value: ref.value,
    on_demand: ref.on_demand,
  };
}

function mapCoreIdentity(
  legacy: LegacyFactory
): Pick<
  FactoryEdit,
  | 'id'
  | 'slug'
  | 'name'
  | 'status'
  | 'verified'
  | 'created_at'
  | 'updated_at'
  | 'country_id'
> {
  const { status, verified } = deriveVisibility(legacy);
  return {
    id: legacy.id,
    slug: legacy.slug ?? '',
    name: legacy.name,
    status,
    verified,
    created_at: legacy.created_at ?? '',
    updated_at: legacy.updated_at ?? '',
    country_id: legacy.country_id ?? null,
  };
}

function mapTaxonomy(
  legacy: LegacyFactory,
  industrialSubtypes: SlugData[],
  capabilities: SlugData[],
  capabilityIdsArray: number[]
): Pick<
  FactoryEdit,
  | 'industrial_area_id'
  | 'industrial_area_name'
  | 'industrial_subtypes'
  | 'industrial_subtype_ids'
  | 'capabilities'
  | 'capability_ids_array'
  | 'capability_ids'
  | 'current_capabilities'
> {
  return {
    industrial_area_id: legacy.industrial_area_id ?? null,
    industrial_area_name: legacy.industrial_area_name ?? null,
    industrial_subtypes: industrialSubtypes,
    industrial_subtype_ids:
      legacy.industrial_subtype_ids ??
      industrialSubtypes.map((subtype) => subtype.id),
    capabilities,
    capability_ids_array: [...capabilityIdsArray].sort((a, b) => a - b),
    capability_ids: collectLegacyLeafIds(legacy),
    current_capabilities: legacy.current_capabilities ?? [],
  };
}

function mapSiteAndProfile(
  legacy: LegacyFactory
): Pick<
  FactoryEdit,
  | 'address'
  | 'po_box'
  | 'latitude'
  | 'longitude'
  | 'description'
  | 'creation_date'
  | 'website'
  | 'linked_in_url'
  | 'logo'
  | 'images'
  | 'shareholder_uae'
  | 'product_reference'
  | 'activity_reference'
> {
  return {
    address: legacy.address ?? null,
    po_box: legacy.po_box ?? null,
    latitude: parseCoordinate(legacy.latitude),
    longitude: parseCoordinate(legacy.longitude),
    description: legacy.description ?? null,
    creation_date: String(legacy.creation_date ?? ''),
    website: legacy.website ?? '',
    linked_in_url: legacy.linked_in_url ?? legacy.linkedin ?? null,
    logo: legacy.logo ?? null,
    images: legacy.images ?? [],
    shareholder_uae: legacy.shareholder_uae ?? null,
    product_reference: legacy.product_reference ?? null,
    activity_reference: legacy.activity_reference ?? null,
  };
}

function mapCommercialAndPeople(
  legacy: LegacyFactory
): Pick<
  FactoryEdit,
  | 'project_references'
  | 'project_ref_on_demand'
  | 'turnover'
  | 'turnover_on_demand'
  | 'company_size'
  | 'headcount_production'
  | 'headcount_management'
  | 'workshop_used'
  | 'workshop_available'
  | 'trade_license_number'
  | 'certification'
  | 'contacts'
  | 'prospects'
> {
  return {
    project_references: (legacy.project_references ?? []).map(
      mapProjectReference
    ),
    project_ref_on_demand: legacy.project_ref_on_demand ?? false,
    turnover: parseTurnover(legacy.turnover),
    turnover_on_demand: legacy.turnover_on_demand ?? false,
    company_size: legacy.company_size ?? '',
    headcount_production: legacy.headcount_production ?? null,
    headcount_management: legacy.headcount_management ?? null,
    workshop_used: legacy.workshop_used ?? null,
    workshop_available: legacy.workshop_available ?? null,
    trade_license_number: legacy.trade_license_number ?? null,
    certification: parseCertification(legacy.certification),
    contacts: (legacy.contacts ?? []).map(mapContact),
    prospects: (legacy.prospects ?? []).map(mapContact),
  };
}

function mapLegacyFactoryFields(legacy: LegacyFactory): FactoryEdit {
  const industrialSubtypes = toSlugData(legacy.industrial_subtypes);
  const capabilities = toSlugData(legacy.capabilities);
  const capabilityIdsArray =
    legacy.capability_ids_array ?? capabilities.map((cap) => cap.id);

  return {
    ...mapCoreIdentity(legacy),
    ...mapTaxonomy(
      legacy,
      industrialSubtypes,
      capabilities,
      capabilityIdsArray
    ),
    ...mapSiteAndProfile(legacy),
    ...mapCommercialAndPeople(legacy),
  };
}

/**
 * Maps a legacy factory (or already-domain MSW payload) to {@link FactoryEdit}.
 */
export function legacyToFactoryEdit(wire: unknown): FactoryEdit {
  if (isDomainFactoryEdit(wire)) {
    return wire;
  }
  // HTTP JSON is untyped; domain payloads are handled above.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- legacy wire
  return mapLegacyFactoryFields(wire as LegacyFactory);
}

const WRITE_DROP_KEYS = new Set([
  'gallery_image_ids',
  'status',
  'can_edit',
  'capability_ids',
  'capabilities',
  'capability_ids_array',
  'industrial_subtypes',
  'project_references',
  'contacts',
  'prospects',
  'images',
  'logo',
  'slug',
  'created_at',
  'updated_at',
  'verified',
  'current_capabilities',
  'id',
]);

function certificationToWire(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .join(', ');
  }
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

/**
 * Builds the legacy `{ factory: … }` write envelope from a domain payload.
 *
 * Drops UI-only / read-only fields. Renames `linked_in_url` → `linkedin` and
 * joins `certification` arrays into a comma-separated string.
 */
export function factoryEditToLegacy(
  entity: Partial<FactoryEdit> | FactoryEditPayload
): LegacyFactoryWriteEnvelope {
  const factory: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(entity)) {
    if (WRITE_DROP_KEYS.has(key)) {
      continue;
    }
    if (typeof value === 'undefined') {
      continue;
    }
    if (key === 'linked_in_url') {
      factory['linkedin'] = value;
      continue;
    }
    if (key === 'certification') {
      factory['certification'] = certificationToWire(value);
      continue;
    }
    factory[key] = value;
  }

  return { factory };
}
